import type { ReactNode } from "react";
import { ByGroupTeammateGroupFilterItemContainer } from "./byGroupTeammateGroupFilterItemContainer.js";
import { CoachingGoalFilterItem } from "./coachingGoalFilterItem.js";
import { CustomFieldsFilterItem } from "./customFieldsFilterItem.js";
import { DateRangeFilterItem } from "./dateRangeFilterItem.js";
import type { FilterParams } from "./filterParams.js";
import type { FilterKey, FilterValues } from "./filterState.js";
import { OutcomeFilterItem } from "./outcomeFilterItem.js";
import { PlaybookConditionsFilterItem } from "./playbookConditionsFilterItem.js";
import { SignalFilterItem } from "./signalFilterItem.js";
import { SignalMetadataFilterItem } from "./signalMetadataFilterItem.js";
import { TeammateGroupFilterItemContainer } from "./teammateGroupFilterItemContainer.js";
import { TotalRecordingDurationFilterItem } from "./totalRecordingDurationFilterItem.js";

type FilterRendererMapping = {
  [F in FilterKey]: FilterRenderer<F>;
};

type FilterRenderer<F extends FilterKey> = (props: {
  params: FilterParams;
  filterKeys: readonly FilterKey[];
  value: FilterValues[F];
  onChange: (newValue: FilterValues[F]) => void;
}) => ReactNode;

export const filterRenderers: FilterRendererMapping = {
  teammateGroupIds: ({ params, value, onChange }) => (
    <TeammateGroupFilterItemContainer
      teammateId={params.teammateId}
      value={value}
      onChange={onChange}
    />
  ),
  byGroupTeammateGroupIds: ({ value, onChange }) => (
    <ByGroupTeammateGroupFilterItemContainer
      value={value}
      onChange={onChange}
    />
  ),
  dateRange: ({ params, filterKeys, value, onChange }) => (
    <DateRangeFilterItem
      playbookId={params.playbookId}
      hasBasisPicker={filterKeys.includes("dateBasis")}
      value={value}
      onChange={onChange}
    />
  ),
  // Rendered as part of the date-range chip, so it has no chip of its own.
  dateBasis: () => null,
  playbookConditions: ({ params, value, onChange }) => (
    <PlaybookConditionsFilterItem
      playbookId={params.playbookId}
      value={value}
      onChange={onChange}
    />
  ),
  customFields: ({ value, onChange }) => (
    <CustomFieldsFilterItem value={value} onChange={onChange} />
  ),
  outcome: ({ params, value, onChange }) => (
    <OutcomeFilterItem
      playbookId={params.playbookId}
      value={value}
      onChange={onChange}
    />
  ),
  signalId: ({ value, onChange }) => (
    <SignalFilterItem value={value} onChange={onChange} />
  ),
  signalMetadataFields: ({ params, value, onChange }) => (
    <SignalMetadataFilterItem
      params={params}
      value={value}
      onChange={onChange}
    />
  ),
  totalRecordingDuration: ({ value, onChange }) => (
    <TotalRecordingDurationFilterItem value={value} onChange={onChange} />
  ),
  coachingGoal: ({ value, onChange }) => (
    <CoachingGoalFilterItem value={value} onChange={onChange} />
  ),
};
